import { useCallback, useEffect, useMemo, useState } from "react";
import { Select, SelectItem } from "@heroui/react";

const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const getTodayDate = () => toYmd(new Date());

/** Single selected day, sent as both bounds so existing report APIs stay compatible. */
export const getRangeDates = (date = getTodayDate()) => ({
  start: date,
  end: date,
});

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: "Bearer " + localStorage.getItem("jwt"),
});

const ALL_USERS_KEY = "all";

const ReportFilters = ({
  showMarket = true,
  showMatch = true,
  showOperator = true,
  showSession = true,
  showGroupLevel = false,
  showUser = false,
  onApply,
}) => {
  const [selectedDate, setSelectedDate] = useState(() => getTodayDate());
  const [markets, setMarkets] = useState([]);
  const [matches, setMatches] = useState([]);
  const [operators, setOperators] = useState([]);
  const [users, setUsers] = useState([]);
  const [marketId, setMarketId] = useState("all");
  const [matchId, setMatchId] = useState("all");
  const [operatorId, setOperatorId] = useState("all");
  const [session, setSession] = useState("all");
  const [groupLevel, setGroupLevel] = useState("summary");
  const [selectedUserKeys, setSelectedUserKeys] = useState(
    () => new Set([ALL_USERS_KEY])
  );

  const dateRange = getRangeDates(selectedDate);

  const selectedUserIds = useMemo(() => {
    const keys = [...selectedUserKeys];
    if (!keys.length || keys.includes(ALL_USERS_KEY)) return [];
    return keys;
  }, [selectedUserKeys]);

  const loadMarkets = useCallback(async () => {
    if (!showMarket) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/markets`,
        { headers: authHeaders() }
      );
      const result = await response.json();
      setMarkets(result?.data || []);
    } catch (error) {
      console.error("Failed to load markets:", error);
      setMarkets([]);
    }
  }, [showMarket]);

  const loadOperators = useCallback(async () => {
    if (!showOperator) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/operators`,
        { headers: authHeaders() }
      );
      const result = await response.json();
      setOperators(result?.data || []);
    } catch (error) {
      console.error("Failed to load operators:", error);
      setOperators([]);
    }
  }, [showOperator]);

  const loadMatches = useCallback(async () => {
    if (!showMatch || !dateRange.start || !dateRange.end) return;

    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      if (marketId !== "all") {
        params.set("market_id", marketId);
      }

      const matchRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/reports/matches?${params.toString()}`,
        { headers: authHeaders() }
      ).then((r) => r.json());

      setMatches(matchRes?.data || []);
    } catch (error) {
      console.error("Failed to load report matches:", error);
      setMatches([]);
    }
  }, [dateRange.start, dateRange.end, marketId, showMatch]);

  const loadUsers = useCallback(async () => {
    if (!showUser || !dateRange.start || !dateRange.end) return;

    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      if (marketId !== "all") params.set("market_id", marketId);
      if (matchId !== "all") params.set("match_id", matchId);
      if (operatorId !== "all") params.set("operator_id", operatorId);
      if (session !== "all") params.set("status", session);

      const userRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/reports/users?${params.toString()}`,
        { headers: authHeaders() }
      ).then((r) => r.json());

      setUsers(userRes?.data || []);
      setSelectedUserKeys(new Set([ALL_USERS_KEY]));
    } catch (error) {
      console.error("Failed to load report users:", error);
      setUsers([]);
      setSelectedUserKeys(new Set([ALL_USERS_KEY]));
    }
  }, [
    showUser,
    dateRange.start,
    dateRange.end,
    marketId,
    matchId,
    operatorId,
    session,
  ]);

  useEffect(() => {
    loadMarkets();
  }, [loadMarkets]);

  useEffect(() => {
    loadOperators();
  }, [loadOperators]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleUserSelectionChange = (keys) => {
    const next = keys === "all" ? new Set([ALL_USERS_KEY]) : new Set(keys);

    if (next.size === 0) {
      setSelectedUserKeys(new Set([ALL_USERS_KEY]));
      return;
    }

    const hadAll = selectedUserKeys.has(ALL_USERS_KEY);
    const hasAll = next.has(ALL_USERS_KEY);

    if (hasAll && next.size > 1) {
      if (!hadAll) {
        // User just clicked All Users → keep only All
        setSelectedUserKeys(new Set([ALL_USERS_KEY]));
      } else {
        // User clicked a specific user while All was selected → drop All
        next.delete(ALL_USERS_KEY);
        setSelectedUserKeys(next.size ? next : new Set([ALL_USERS_KEY]));
      }
      return;
    }

    setSelectedUserKeys(next);
  };

  const buildFilters = () => {
    const range = getRangeDates(selectedDate);
    const userIds = selectedUserIds;
    return {
      date: selectedDate,
      start_date: range.start,
      end_date: range.end,
      market_id: marketId,
      match_id: matchId,
      operator_id: operatorId,
      status: session,
      group_level: groupLevel,
      user_id: userIds.length ? userIds.join(",") : "all",
      user_ids: userIds,
    };
  };

  const handleApply = () => {
    onApply?.(buildFilters());
  };

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userSelectLabel = selectedUserIds.length
    ? `${selectedUserIds.length} user${selectedUserIds.length > 1 ? "s" : ""} selected`
    : "All Users";

  return (
    <div className="rounded border border-gray-200 bg-white p-4 space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Date
          </label>
          <input
            type="date"
            className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[160px]"
            value={selectedDate}
            onChange={(e) => {
              const next = e.target.value;
              if (!next) return;
              setSelectedDate(next);
              setMatchId("all");
            }}
          />
        </div>

        {showMarket && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Market
            </label>
            <select
              className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[160px]"
              value={marketId}
              onChange={(e) => {
                setMarketId(e.target.value);
                setMatchId("all");
              }}
            >
              <option value="all">All Markets</option>
              {markets.map((m) => (
                <option key={m.id || m.market_id} value={m.id || m.market_id}>
                  {m.name || m.market_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {showMatch && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Match
            </label>
            <select
              className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[160px]"
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
            >
              <option value="all">All Matches</option>
              {matches.map((m) => (
                <option key={m.id || m.match_id} value={m.id || m.match_id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {showOperator && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Operator
            </label>
            <select
              className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[160px]"
              value={operatorId}
              onChange={(e) => setOperatorId(e.target.value)}
            >
              <option value="all">All Operators</option>
              {operators.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.operator_id}
                </option>
              ))}
            </select>
          </div>
        )}

        {showSession && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Session
            </label>
            <select
              className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[120px]"
              value={session}
              onChange={(e) => setSession(e.target.value)}
            >
              <option value="all">All</option>
              <option value="0">Open</option>
              <option value="1">Close</option>
            </select>
          </div>
        )}

        {showUser && (
          <div className="min-w-[220px] max-w-[320px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              User ID
            </label>
            <Select
              aria-label="Select users"
              selectionMode="multiple"
              selectedKeys={selectedUserKeys}
              onSelectionChange={handleUserSelectionChange}
              placeholder="All Users"
              className="w-full"
              classNames={{
                trigger:
                  "min-h-10 border border-gray-300 rounded bg-white shadow-none",
                value: "text-sm",
              }}
              renderValue={() => (
                <span className="text-sm text-gray-800">{userSelectLabel}</span>
              )}
            >
              <SelectItem key={ALL_USERS_KEY} textValue="All Users">
                All Users
              </SelectItem>
              {users.map((u) => (
                <SelectItem
                  key={String(u.id)}
                  textValue={`${u.name || u.id} (${u.id})`}
                >
                  {u.name || u.id} ({u.id})
                </SelectItem>
              ))}
            </Select>
          </div>
        )}

        {showGroupLevel && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Group By
            </label>
            <select
              className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[160px]"
              value={groupLevel}
              onChange={(e) => setGroupLevel(e.target.value)}
            >
              <option value="summary">User Summary</option>
              <option value="market">By Market</option>
              <option value="match">By Match</option>
            </select>
          </div>
        )}

        <button
          type="button"
          className="bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700"
          onClick={handleApply}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default ReportFilters;
